import React from "react";
import PropTypes from "prop-types";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "@helpers/translate";
import { formatOrderDate } from "@helpers/formatOrders";
import { CustomScrollTable } from "../../CustomScrollTable";
import { useOrdersLayout } from "../OrdersLayoutContext";
import { FavoriteStarIcon } from "./FavoriteStarIcon";
import { CommunityMobileList } from "./CommunityMobileList";
import { getClickableProps } from "./rowProps";
import { BADGE_ICONS, COIN_ICONS } from "./communityMockData";
import "./CommunityTables.sass";

export const BestTradesTable = ({ data = [], loading = false, onRowClick }) => {
  const { __ } = useTranslation();
  const isMobileQuery = useMediaQuery("(max-width: 768px)");
  const { isCompactCards } = useOrdersLayout();
  const isMobile = isMobileQuery || isCompactCards;

  if (isMobile) {
    return (
      <CommunityMobileList
        data={data}
        loading={loading}
        type="best-trades"
        onItemClick={onRowClick}
      />
    );
  }

  return (
    <CustomScrollTable className="community-table community-table--best-trades">
      <Table stickyHeader>
        <TableHead>
          <TableRow className="community-table__head-row">
            <TableCell className="community-table__col-bt-rank">
              {__("futures.community.rank")}
            </TableCell>
            <TableCell className="community-table__col-bt-user">
              {__("futures.community.user")}
            </TableCell>
            <TableCell className="community-table__col-bt-date">
              {__("futures.community.date")}
            </TableCell>
            <TableCell className="community-table__col-bt-trade">
              {__("futures.community.trade")}
            </TableCell>
            <TableCell className="community-table__col-bt-wager">
              {__("futures.community.wager")}
            </TableCell>
            <TableCell className="community-table__col-bt-entry">
              {__("futures.community.entry")}
            </TableCell>
            <TableCell className="community-table__col-bt-profit">
              {__("futures.community.profit")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => {
            const isProfit = String(row.profit).startsWith("+");
            const isLoss = String(row.profit).startsWith("-");
            const profitClass = isProfit
              ? "community-table__cell--green"
              : isLoss
                ? "community-table__cell--red"
                : "";

            const badgeSrc = BADGE_ICONS[row.badgeIcon];
            const assetIcon =
              COIN_ICONS[row.trade?.assetIcon] ||
              COIN_ICONS[row.trade?.symbol] ||
              null;

            return (
              <TableRow
                key={row.rank ?? index}
                {...getClickableProps(
                  onRowClick,
                  row,
                  "community-table__body-row",
                  "community-table__body-row--clickable",
                )}
              >
                {/* 1. Rank with optional 3D badge */}
                <TableCell className="community-table__col-bt-rank">
                  <div className="community-table__bt-rank-cell">
                    <div className="community-table__bt-badge-box">
                      {badgeSrc ? (
                        <img
                          src={badgeSrc}
                          alt={`rank ${row.rank}`}
                          className="community-table__bt-badge-icon"
                        />
                      ) : null}
                    </div>
                    <span className="community-table__bt-rank-num">
                      {row.rank}
                    </span>
                  </div>
                </TableCell>

                {/* 2. User */}
                <TableCell className="community-table__col-bt-user">
                  <div className="community-table__bt-user-cell">
                    <button
                      type="button"
                      className="community-table__star-btn"
                      aria-label="favorite"
                    >
                      <FavoriteStarIcon
                        active={row.favoriteIcon === "star-yellow"}
                        size={24}
                      />
                    </button>
                    <span className="community-table__bt-username">
                      {row.user}
                    </span>
                  </div>
                </TableCell>

                {/* 3. Date */}
                <TableCell className="community-table__col-bt-date">
                  {formatOrderDate(row.created_at) ?? "—"}
                </TableCell>

                {/* 4. Trade (Coin icon + symbol) */}
                <TableCell className="community-table__col-bt-trade">
                  <div className="community-table__bt-trade-cell">
                    {assetIcon && (
                      <img
                        src={assetIcon}
                        alt={row.trade?.symbol || "asset"}
                        className="community-table__bt-coin-icon"
                      />
                    )}
                    <span className="community-table__bt-coin-name">
                      {row.trade?.symbol}
                    </span>
                  </div>
                </TableCell>

                {/* 5. Wager */}
                <TableCell className="community-table__col-bt-wager">
                  {row.wager}
                </TableCell>

                {/* 6. Entry */}
                <TableCell className="community-table__col-bt-entry">
                  {row.entry}
                </TableCell>

                {/* 7. Profit */}
                <TableCell
                  className={`community-table__col-bt-profit ${profitClass}`}
                >
                  {row.profit}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </CustomScrollTable>
  );
};

BestTradesTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  onRowClick: PropTypes.func,
};

export default BestTradesTable;
